import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useStores } from "../../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge, Box, Container, Paper } from "@mui/material";
import { observer } from "mobx-react";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { SortType } from "../../../../../../models/SortType";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";
import {
  FilterMenuType,
  FilterSidebarMenu,
} from "../../../../../../models/FilterMenu";
import { ModuleType } from "../../../../../../models/ModuleType";
import FilterSidebar from "../../../../views/components/filter/FilterSidebar";
import UnpaidSampleTable from "../../../components/projects/UnpaidSampleTable";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import { UnpaidSampleFilters } from "../../../../../../models/ProjectManagement/UnpaidSampleFilters";
import UnpaidSamplePickupUpdatePopup from "./UnpaidSamplePickupUpdatePopup";
import {
  UnpaidSamplesReturnInfoUpdate,
  UnpaidSamplesReturnInfo,
} from "../../../../../../models/ProjectManagement/UnpaidSample";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import dayjs from "dayjs";
import { ProjectTypeFilter } from "../../../../../../models/ProjectManagement/ProjectManagementFilters";
import { ImageData } from "../../../../../../models/Image/Image";
import PermissionBlockLabel from "../../../../../../views/components/common/pop-up/PermissionBlockLabel ";

const UnpaidSampleReturnListing: React.FC = () => {
  const { userStore, projectManagementStore } = useStores();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterCount, setFilterCount] = useState(0);
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [unpaidSampleActionPopup, setUnpaidSampleActionPopup] =
    useState<boolean>(false);
  const getArrayFromUrlParam = (param: string | null): FilterValue[] => {
    if (!param) return [];
    return param.split(",").map((item) => {
      return { id: item, value: item };
    });
  };

  const parseIdValuePairs = (param: string): FilterValue[] =>
    param.split(",").map((pair) => {
      const [id, value] = pair.split(":");
      return { id: id || "", value: value || "" };
    });

  const initialFilters: UnpaidSampleFilters = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string) || 1
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
    order_id: searchParams.get("order_id")
      ? getArrayFromUrlParam(searchParams.get("order_id")!)
      : [],
    admin_id: searchParams.get("admin_id")
      ? parseIdValuePairs(searchParams.get("admin_id")!)
      : [],
    order_status: searchParams.get("order_status")
      ? getArrayFromUrlParam(searchParams.get("order_status")!)
      : searchParams.get("active") === "false" &&
        searchParams.get("archive") === "false"
      ? []
      : [{ id: "1", value: "Active" }],
    active:
      searchParams.get("active") !== null
        ? searchParams.get("active") === "true"
        : true,
    archive: searchParams.get("archive") === "true",
  };

  const initialParams: UnpaidSampleFilters = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
    limit: parseInt(searchParams.get("limit") || "30"),
    admin_id: searchParams.get("admin_id")
      ? parseIdValuePairs(searchParams.get("admin_id")!)
      : [],
    order_id: searchParams.get("order_id")
      ? getArrayFromUrlParam(searchParams.get("order_id")!)
      : [],
    order_status: searchParams.get("order_status")
      ? getArrayFromUrlParam(searchParams.get("order_status")!)
      : searchParams.get("active") === "false" &&
        searchParams.get("archive") === "false"
      ? []
      : [{ id: "1", value: "Active" }],
    active:
      searchParams.get("active") !== null
        ? searchParams.get("active") === "true"
        : true,
    archive: searchParams.get("archive") === "true",
  };

  const [filters, setFilters] = useState<UnpaidSampleFilters>(initialFilters);
  const [params, setParams] = useState<UnpaidSampleFilters>(initialParams);

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_UNPAID_RETURN_SAMPLES)) {
      projectManagementStore.getUnpaidSampleReturnListing({
        ...params,
        order_id: params.order_id,
        admin_id: params.admin_id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    updateSelectedFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (query: string) => {
    setSearchTerm(query);
    if (query.length === 0) {
      setSearchTerm("");
      setParams((prev) => ({
        ...prev,
        search_text: "",
        page_no: 1,
      }));
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
          sortBy: filters.sort_by || "created_date",
          sort_type: filters.sort_type || SortType.ASC,
        }));
      } else {
        setSearchTerm("");
        setParams((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };
  const handleFilterToggle = () => {
    setOpenFilter(!openFilter);
  };
  const handleFilterChange = useCallback(
    (
      key: keyof UnpaidSampleFilters,
      value: UnpaidSampleFilters[keyof UnpaidSampleFilters]
    ) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const filterMenu: FilterSidebarMenu[] = useMemo(() => {
    return [
      {
        moduleType: ModuleType.ORDER,
        fieldType: FilterMenuType.MULTI_SELECT,
        subType: "search_order",
        label: "Order",
        placeholder: "Search Order",
        value:
          filters.order_id?.map((id) => ({
            id: typeof id === "number" ? id.toString() : id.id.toString(),
            value: typeof id === "number" ? id.toString() : id.value.toString(),
          })) || [],
        onChange: (value: FilterValue[]) =>
          handleFilterChange(
            "order_id",
            value.map((item) => Number(item.id))
          ),
      },
      {
        moduleType: ModuleType.ADMIN,
        fieldType: FilterMenuType.SEARCH_BAR,
        subType: "search_admin",
        label: "Admin",
        placeholder: "Enter Admin name",
        value: filters.admin_id?.map((id) => {
          return {
            id: id.id.toString(),
            value: id.value,
          };
        }),
        onChange: (value: FilterValue[]) =>
          handleFilterChange("admin_id", value),
      },
      {
        moduleType: ModuleType.PROJECT_MANAGEMENT,
        fieldType: FilterMenuType.LOCAL_MULTI_SELECT,
        label: "Order Status",
        list: ProjectTypeFilter.filter((item) => item.id !== "0"),
        value: filters?.order_status?.map((item) => item.id) || [],
        onChange: (selectedValues: string[]) => {
          setFilters((prev) => {
            const selectedOrderStatus = ProjectTypeFilter.filter((item) =>
              selectedValues.some((selected) => selected === item.id)
            );

            // Set active and archive flags based on selected order status IDs
            const hasActive = selectedValues.includes("1");
            const hasArchive = selectedValues.includes("2");

            return {
              ...prev,
              order_status: selectedOrderStatus,
              active: hasActive,
              archive: hasArchive,
            };
          });
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const updateSelectedFilters = (appliedFilters: UnpaidSampleFilters) => {
    let temp_filters_count = 0;

    if (appliedFilters?.order_id && appliedFilters.order_id.length > 0) {
      temp_filters_count++;
    }

    if (appliedFilters?.admin_id && appliedFilters.admin_id.length > 0) {
      temp_filters_count++;
    }

    if (
      appliedFilters?.order_status &&
      appliedFilters.order_status.length > 0
    ) {
      temp_filters_count++;
    }

    setFilterCount(temp_filters_count);
  };

  const handleApplyFilters = () => {
    const newParams: UnpaidSampleFilters = {
      ...params,
      sort_by: filters.sort_by,
      sort_type: filters.sort_type,
      page_no: 1,
      limit: params.limit,
      order_id: filters.order_id,
      admin_id: filters.admin_id,
      order_status: filters.order_status,
      active: filters.active,
      archive: filters.archive,
    };

    setParams(newParams);
    setFilters(newParams);
    updateSelectedFilters(newParams);
    handleFilterToggle();
  };

  const handleClearFilters = () => {
    const clearedFilters: UnpaidSampleFilters = {
      page_no: 1,
      search_text: undefined,
      sort_by: "created_date",
      sort_type: SortType.DESC,
      order_id: [],
      admin_id: [],
      order_status: [{ id: "1", value: "Active" }],
      active: true,
      archive: false,
    };

    const clearedParams: UnpaidSampleFilters = {
      ...clearedFilters,
      limit: params.limit,
    };

    setFilters(clearedFilters);
    setParams(clearedParams);
    setSearchTerm("");
    updateSelectedFilters(clearedParams);

    handleFilterToggle();
    navigate(
      {
        search: `?page=1&limit=${params.limit}&sort_by=created_date&sort_type=DESC&active=true`,
      },
      { replace: true }
    );
  };

  const handleActionClick = (unpaid_sample_return_details_id: number) => {
    const selectedRecord =
      projectManagementStore.unpaidSampleResponse?.list?.find(
        (record) =>
          record.unpaid_sample_return_details_id ===
          unpaid_sample_return_details_id
      );

    if (selectedRecord) {
      const currentOrderId = selectedRecord.order_id;
      const currentPickupId = unpaid_sample_return_details_id;

      const shouldFetchPickupInfo =
        projectManagementStore.selectedReturnPickupId !== currentPickupId;

      const promises: Promise<UnpaidSamplesReturnInfoUpdate | void>[] = [];

      if (shouldFetchPickupInfo) {
        promises.push(
          projectManagementStore.getUnpaidSampleReturnDetailsById(
            currentOrderId,
            currentPickupId,
          ),
        );
      }

      if (promises.length > 0) {
        Promise.all(promises)
          .then(() => {
            projectManagementStore.setSelectedReturnProductOrderId(
              currentOrderId,
            );
            if (shouldFetchPickupInfo) {
              projectManagementStore.setselectedReturnPickupId(currentPickupId);
            }
            setUnpaidSampleActionPopup(true);
          })
          .catch(() => {
            setUnpaidSampleActionPopup(true);
          });
      } else {
        setUnpaidSampleActionPopup(true);
      }
    }
  };

  useEffect(() => {
    const updateQueryParams = () => {
      const newSearchParams = new URLSearchParams();
      if (params.page_no)
        newSearchParams.set("page", params.page_no.toString());
      if (params.limit) newSearchParams.set("limit", params.limit.toString());
      if (params.sort_by) newSearchParams.set("sort_by", params.sort_by);
      if (params.sort_type) newSearchParams.set("sort_type", params.sort_type);
      if (params.search_text)
        newSearchParams.set("search_text", params.search_text);
      if (params.order_id && params.order_id.length > 0) {
        const orderIds = params.order_id.map((item) =>
          typeof item === "number" ? item.toString() : item.id.toString()
        );
        newSearchParams.set("order_id", orderIds.join(","));
      }
      if (params.admin_id && params.admin_id.length > 0) {
        const adminIds = params.admin_id.map(
          (item) => `${item.id}:${item.value}`
        );
        newSearchParams.set("admin_id", adminIds.join(","));
      }
      if (params.order_status && params.order_status.length > 0) {
        const orderStatus = params.order_status.map((item) =>
          typeof item === "number" ? item : item.id
        );
        newSearchParams.set("order_status", orderStatus.join(","));
      }
      if (params.active !== undefined)
        newSearchParams.set("active", params.active.toString());
      if (params.archive !== undefined)
        newSearchParams.set("archive", params.archive.toString());

      navigate({ search: newSearchParams.toString() }, { replace: true });
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({ ...prev, limit: newLimit, page_no: 1 }));
  };

  const handleUpdateUnpaidSamplePickupDate = (
    pickup_id: number,
    data: UnpaidSamplesReturnInfoUpdate
  ) => {
    setIsLoading(true);
    const existingPickupData =
      projectManagementStore.unpaidSamplePickupUpdateInfoResponce;
    const originalSkuLines = existingPickupData?.products || [];

    const skuLineKey = (productId: number, sku?: string | null) =>
      `${productId}|${String(sku ?? "").trim().toUpperCase()}`;

    // Get the dates to validate - use new data if provided, otherwise use existing data
    const actualPickupDate =
      data.actual_pickup_date || existingPickupData?.actual_pickup_date;
    const actualPackReceivedDate =
      data.actual_pack_received_date ||
      existingPickupData?.actual_pack_received_date;

    // Validate dates if both are present
    if (actualPickupDate && actualPackReceivedDate) {
      if (dayjs(actualPackReceivedDate).isBefore(dayjs(actualPickupDate))) {
        showError(
          "Error",
          "Pack Received Date must be after Actual Pickup Date"
        );
        return;
      }
    }

    for (const selectedProduct of data.products || []) {
      const originalLine = originalSkuLines.find(
        (p) =>
          skuLineKey(p.product_id ?? p.id, p.sku) ===
          skuLineKey(selectedProduct.id, selectedProduct.sku),
      );

      const existingReceivedQuantity =
        existingPickupData?.products?.find(
          (p) =>
            skuLineKey(p.product_id ?? p.id, p.sku) ===
            skuLineKey(selectedProduct.id, selectedProduct.sku),
        )?.returned_quantity ??
        existingPickupData?.products?.find(
          (p) =>
            skuLineKey(p.product_id ?? p.id, p.sku) ===
            skuLineKey(selectedProduct.id, selectedProduct.sku),
        )?.quantity ??
        0;

      const totalQuantity = existingReceivedQuantity + selectedProduct.quantity;
      const expectedQty = Number(originalLine?.expected_quantity ?? 0);

      if (originalLine && selectedProduct.quantity > expectedQty) {
        showError(
          "Error",
          `Selected quantity (${selectedProduct.quantity}) cannot be greater than expected quantity (${expectedQty}) for product ${selectedProduct.id}${selectedProduct.sku ? ` / SKU ${selectedProduct.sku}` : ""}`,
        );
        return;
      }

      if (originalLine && totalQuantity > expectedQty) {
        showError(
          "Error",
          `Total quantity (existing: ${existingReceivedQuantity} + new: ${selectedProduct.quantity} = ${totalQuantity}) cannot be greater than expected quantity (${expectedQty}) for product ${selectedProduct.id}${selectedProduct.sku ? ` / SKU ${selectedProduct.sku}` : ""}`,
        );
        return;
      }
    }

    projectManagementStore
      .updateUnpaidSamplePickupInfoUpdate(
        projectManagementStore.selectedReturnProductOrderId,
        pickup_id,
        data
      )
      .then((response) => {
        if (response?.success) {
          if (response.result) {
            projectManagementStore.unpaidSamplePickupUpdateInfoResponce =
              response.result;

            projectManagementStore.setUnpaidSamplePickupUpdateFormData({
              ...projectManagementStore.unpaidSamplePickupUpdateFormData,
              ...response.result,
            });

            const recordToUpdate =
              projectManagementStore.unpaidSampleResponse?.list.find(
                (item) => item.unpaid_sample_return_details_id === pickup_id
              );

            if (recordToUpdate) {
              recordToUpdate.actual_pickup_date =
                response.result.actual_pickup_date;
              recordToUpdate.actual_pack_received_date =
                response.result.actual_pack_received_date;
            }
          }

          showSuccess("Unpaid sample details updated successfully");
        } else {
          showError(
            "Error",
            response?.message || "Failed to update unpaid sample details"
          );
        }
        setUnpaidSampleActionPopup(false);
      })
      .catch((error: any) => {
        showError(
          "Error",
          error?.response?.data?.message ||
            error?.message ||
            "Something went wrong"
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeleteImage = (image: ImageData) => {
    setIsLoading(true);

    const formData = projectManagementStore.unpaidSamplePickupUpdateFormData;

    const originalImage = formData.image_urls?.find(
      (url) => url.image_url === image.img
    );

    if (!originalImage) {
      showError("Error", "Image not found");
      setIsLoading(false);
      return;
    }

    projectManagementStore
      .deleteUnpaidSampleProductImage(
        Number(projectManagementStore.selectedReturnPickupId),
        originalImage.image_url,
        originalImage.id
      )
      .then(() => {
        // Update local state after successful API call
        if (formData.image_urls) {
          const updatedImageUrls = formData.image_urls.filter(
            (url) => url.id !== originalImage.id
          );

          projectManagementStore.setUnpaidSamplePickupUpdateFormData({
            ...formData,
            image_urls: updatedImageUrls as typeof formData.image_urls,
          });

          // Update the response data as well
          if (
            projectManagementStore.unpaidSamplePickupUpdateInfoResponce
              ?.image_urls
          ) {
            projectManagementStore.unpaidSamplePickupUpdateInfoResponce.image_urls =
              projectManagementStore.unpaidSamplePickupUpdateInfoResponce.image_urls.filter(
                (url) => url.id !== originalImage.id
              ) as typeof projectManagementStore.unpaidSamplePickupUpdateInfoResponce.image_urls;
          }
        }
      })
      .catch((error) => {
        showError("Error", "Failed to delete image");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Container
      sx={{
        paddingTop: 2,
      }}
      maxWidth="xl"
    >
      {!userStore.hasPermission(EmployeeRoles.VIEW_UNPAID_RETURN_SAMPLES) ? (
        <PermissionBlockLabel />
      ) : (
        <>
          <Paper
            sx={{
              padding: 5,
              paddingBottom: 3,
            }}
          >
            <CustomTypography variant="heading3" style={{ fontWeight: 700 }}>
              Unpaid Samples Listing
            </CustomTypography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                paddingBottom: 3,
                paddingTop: 3,
                justifyContent: "space-between",
                gap: 4,
              }}
            >
              <CustomTextField
                value={params.search_text ? params.search_text : searchTerm}
                onSearch={(query) => handleSearch(query)}
                onKeyDown={handleKeyDown}
                width={"100%"}
                placeholder="Search Order ID"
              />
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <AppButton
                  variant="grayscale"
                  label="Filter"
                  startIcon={<FilterAltOutlinedIcon />}
                  onClick={() => {
                    handleFilterToggle();
                  }}
                  customStyles={{ width: "130px" }}
                />
                {filterCount > 0 && (
                  <Badge
                    color="primary"
                    badgeContent={filterCount}
                    sx={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      transform: "translate(50%, -50%)",
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box>
              <UnpaidSampleTable
                onActionClick={(unpaid_sample_return_details_id: number) =>
                  handleActionClick(unpaid_sample_return_details_id)
                }
                maxHeight={window.innerHeight - 350}
                data={projectManagementStore.unpaidSampleResponse?.list || []}
                limit={params.limit || 30}
                page_no={params.page_no || 1}
                total_count={
                  projectManagementStore.unpaidSampleResponse?.total_count || 0
                }
                total_pages={
                  projectManagementStore.unpaidSampleResponse?.total_pages || 0
                }
                sortBy={params.sort_by || "created_date"}
                sortType={params.sort_type as SortType}
                onPageChange={(newPage: number) => {
                  setParams((prev) => ({ ...prev, page_no: newPage }));
                }}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSortChange={(column, sortType) => {
                  setParams((prev) => ({
                    ...prev,
                    sort_by: column,
                    sort_type: sortType,
                  }));
                }}
              />
            </Box>
          </Paper>
          {openFilter && (
            <FilterSidebar
              isOpen={openFilter}
              items={filterMenu}
              onApply={handleApplyFilters}
              handleClear={handleClearFilters}
              toggleFilter={handleFilterToggle}
              disablePastDates={false}
            />
          )}
          <UnpaidSamplePickupUpdatePopup
            orderId={projectManagementStore.selectedReturnProductOrderId}
            open={unpaidSampleActionPopup}
            existingPickupData={
              projectManagementStore.unpaidSamplePickupUpdateInfoResponce ||
              null
            }
            onClose={() => {
              setUnpaidSampleActionPopup(false);
            }}
            onSave={(data, pickup_id) => {
              return handleUpdateUnpaidSamplePickupDate(pickup_id, data);
            }}
            onDeleteImage={handleDeleteImage}
          />
        </>
      )}
    </Container>
  );
};

export default observer(UnpaidSampleReturnListing);
